"use client";

import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
// Locale-aware `Link`: as a raw <a> this CTA emitted a locale-LESS path, so
// every "閱讀全文" / "閱讀全部專欄" click paid a middleware redirect round-trip.
// Every call site passes an in-app route.
import { Link } from "@/i18n/routing";
import { EJEDU_COLORS, EJEDU_TYPE } from "@/lib/theme/ej-education";

interface LinkProps {
  label: string;
  /**
   * REQUIRED, and deliberately has no default.
   *
   * It used to default to `"#"`, and three call sites took it — so the CTA
   * rendered, invited a click and went nowhere. Worse, `globals.css` gates the
   * hover colour behind `:not([href="#"])`, so the hover work already written
   * into this component could never fire on those three, which is what QA
   * filed. Making it required means the compiler finds a bare call site
   * instead of a reviewer.
   */
  href: string;
  onClick?: () => void;
  variant?: "primary" | "onDark";
  icon?: ReactNode;
}

/** Translate-x arrow CTA — GSG signature interaction. */
export function ArrowLink({ label, href, variant = "primary", icon }: LinkProps) {
  const onDark = variant === "onDark";
  const color = onDark ? EJEDU_COLORS.textOnDark : EJEDU_COLORS.primary;
  return (
    // `hkej-tap hkej-tap-title hkej-tap-self` (app/styles/globals.css): the
    // colour below is an inline style, which no Tailwind `hover:text-*` can
    // beat — that is why QA saw "懸停時缺少顏色變化和箭頭移動效果". The `-self`
    // variant is required because the hovered element IS the coloured one; the
    // plain descendant selector cannot reach it.
    //
    // The arrow translate and the underline already worked and are unchanged.
    <Link
      href={href}
      className="group hkej-tap hkej-tap-title hkej-tap-self inline-flex items-center gap-2"
      style={
        {
          ...EJEDU_TYPE.button,
          color,
          textUnderlineOffset: 4,
          // On dark surfaces staying white is correct — only the underline
          // should change. Elsewhere darken toward the brand colour.
          ["--hkej-hover" as string]: onDark
            ? EJEDU_COLORS.textOnDark
            : EJEDU_COLORS.primaryDark,
        } as React.CSSProperties
      }
    >
      <span>{label}</span>
      <span className="inline-flex transition-transform duration-300 ease-out group-hover:translate-x-1">
        {icon ?? <ArrowRight className="h-4 w-4" />}
      </span>
    </Link>
  );
}
